import {
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';
import { useToastStore } from '../components/Toast';
import { ApiError } from './api';

/**
 * Shared TanStack Query client.
 *
 * - `staleTime` 5 min: pack lists and metadata are mostly read-only between
 *   admin actions, so we avoid hammering the Worker on every focus.
 * - `gcTime` 30 min: keep cached responses around long enough that navigating
 *   between the list and editor doesn't refetch on every visit.
 * - `retry` 1: the dashboard surfaces server errors via a global error
 *   boundary (task 11.3); the Worker error envelope already carries a
 *   request id, so retrying once is enough to mask transient blips without
 *   masking real failures.
 * - `throwOnError: false`: we let the global `QueryCache.onError` and
 *   `MutationCache.onError` handlers below push a toast and let component
 *   code render fallback UI from the `error` field instead of unmounting
 *   the tree via React's error boundary.
 *
 * The toast hook here is a fallback for cases where `apiFetch` was bypassed
 * or where the caller passed `silent: true`. `apiFetch` already pushes a
 * toast for non-silent errors; the cache handler dedupes by skipping
 * `network_down` errors that the offline guard has already surfaced.
 *
 * Validates Requirements 25.1, 25.2.
 */

function pushToastForCacheError(error: unknown): void {
  if (!(error instanceof ApiError)) {
    return;
  }
  // `apiFetch` has already pushed a toast for non-silent errors; only
  // step in for the cases it deliberately skipped (silent calls that
  // still want global error visibility), and for the `network_down`
  // sentinel which is already covered by the network banner.
  if (error.code === 'network_down') {
    return;
  }
  useToastStore.getState().addToast({
    severity: 'error',
    title: 'Lỗi',
    message: error.message,
    requestId: error.requestId,
  });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
      throwOnError: false,
    },
    mutations: {
      retry: 0,
      throwOnError: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => pushToastForCacheError(error),
  }),
  mutationCache: new MutationCache({
    onError: (error) => pushToastForCacheError(error),
  }),
});
